import { useParams, Link } from 'react-router-dom';
import { categories, comics } from '@/mocks/comics';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { useState, useEffect } from 'react';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const [showBackTop, setShowBackTop] = useState(false);

  useEffect(() => {
    if (slug) setActiveCategory(slug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const filteredComics = activeCategory === 'all'
    ? comics
    : comics.filter((c) => c.categories.includes(activeCategory));

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const pageTitle = currentCategory ? `${currentCategory.name}漫画` : '全部漫画';
  const pageDescription = currentCategory
    ? currentCategory.description
    : '浏览飞飞漫画网全部免费漫画资源';

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '分类浏览', href: '/category/guoman' },
            ...(currentCategory ? [{ label: currentCategory.name }] : []),
          ]}
        />

        <section className="py-6 md:py-10">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-1">
              {pageTitle}
            </h1>
            <p className="text-sm text-foreground-500 mb-6">{pageDescription}</p>

            <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-3 mb-6">
              <button
                onClick={() => setActiveCategory('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  activeCategory === 'all'
                    ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                全部
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {filteredComics.map((comic) => (
                  <div key={comic.id} className="flex justify-center">
                    <ComicCard
                      id={comic.id}
                      title={comic.title}
                      author={comic.author}
                      cover={comic.cover}
                      category={comic.category}
                      categories={comic.categories}
                      rating={comic.rating}
                      views={comic.views}
                      status={comic.status}
                      chapters={comic.chapters}
                      size="md"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary-100 flex items-center justify-center">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-emotion-sad-line text-2xl text-foreground-300"></i>
                  </div>
                </div>
                <p className="text-foreground-500">该分类暂无漫画，请查看其他分类</p>
                <button
                  onClick={() => setActiveCategory('all')}
                  className="mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
                >
                  查看全部漫画
                </button>
              </div>
            )}
          </div>
        </section>

        {currentCategory && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-lg font-heading text-foreground-950 mb-4">
                推荐阅读
              </h2>
              <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-2">
                {comics
                  .filter((c) => c.id !== filteredComics[0]?.id)
                  .sort(() => Math.random() - 0.5)
                  .slice(0, 6)
                  .map((comic) => (
                    <ComicCard
                      key={comic.id}
                      id={comic.id}
                      title={comic.title}
                      author={comic.author}
                      cover={comic.cover}
                      category={comic.category}
                      categories={comic.categories}
                      rating={comic.rating}
                      views={comic.views}
                      status={comic.status}
                      size="md"
                    />
                  ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`fixed bottom-8 right-8 z-40 w-10 h-10 rounded-full bg-primary-500 text-background-50 flex items-center justify-center shadow-lg hover:bg-primary-600 transition-all duration-300 cursor-pointer ${
          showBackTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
        aria-label="回到顶部"
      >
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-arrow-up-line text-lg"></i>
        </div>
      </button>
    </>
  );
}